import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

const inputVariants = cva(
  cn(
    'flex w-full text-sm lg:text-base transition-colors file:border-0 file:bg-transparent file:text-sm placeholder:text-[#9E9E9E] text-black relative border border-border',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
    'border-box items-center gap-2',
  ),
  {
    variants: {
      variant: {
        transparent: ['bg-transparent', 'ea-input__transparent'],
        filled: ['bg-white', 'ea-input__filled'],
      },

      rounded: {
        default: ['rounded-[6px]', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },

    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);
export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  before?: React.ReactNode;
  after?: React.ReactNode;
  floatingLabel?: boolean;
  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
  classNames?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      before,
      after,
      afterOuter,
      beforeOuter,
      classNames,
      floatingLabel,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({
              rounded: props.rounded,
              variant: props.variant,
            }),
            'ea-input__wrapper',
            'h-[52px]',
            props['aria-invalid'] &&
              'ea-input__wrapper--invalid border-destructive text-destructive',
            className?.wrapper,
            classNames?.wrapper,
          )}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 flex h-full items-center pl-4',
              className?.before,
              classNames?.before,
            )}
          >
            {before}
          </div>
          <input
            type={type}
            className={cn(
              'h-full w-full flex-1 overflow-hidden bg-transparent px-4 hover:outline-none focus:outline-none',
              'text-black file:h-full file:cursor-pointer file:border-none file:border-input file:bg-secondary file:outline-none placeholder:text-[#666666] file:hover:bg-secondary/90',
              !!before && 'pl-9',
              !!after && 'pr-9',
              'ea-input__main',
              floatingLabel &&
                cn(
                  'ea-input__main--floating-label',
                  'h-[50px] rounded-none pt-[10px] sm:h-[52px] sm:pt-[10px] lg:h-[54px]',
                ),
              className?.main,
              classNames?.main,
            )}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              'absolute inset-y-0 right-0 flex h-full items-center pr-1',
              className?.after,
              classNames?.after,
            )}
          >
            {after}
          </div>
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
